import { __rest, __assign } from '../../node_modules/tslib/tslib.es6.js';
import React from 'react';
import { useTheme, Button, Spinner, themeGet } from '@primer/react';
import { ColorSchemes } from '../../utils/constants.js';

// Enums for Size, Variant, Type, and LoadingPosition
var LTButtonSize;
(function (LTButtonSize) {
  LTButtonSize["LARGE"] = "large";
  LTButtonSize["MEDIUM"] = "medium";
  LTButtonSize["SMALL"] = "small";
})(LTButtonSize || (LTButtonSize = {}));
var LTButtonVariant;
(function (LTButtonVariant) {
  LTButtonVariant["DANGER"] = "danger";
  LTButtonVariant["DEFAULT"] = "default";
  LTButtonVariant["INVISIBLE"] = "invisible";
  LTButtonVariant["OUTLINE"] = "outline";
  LTButtonVariant["PRIMARY"] = "primary";
})(LTButtonVariant || (LTButtonVariant = {}));
var LTButtonType;
(function (LTButtonType) {
  LTButtonType["DEFAULT"] = "default";
  LTButtonType["FLOATING"] = "floating";
})(LTButtonType || (LTButtonType = {}));
var LTButtonLoadingPosition;
(function (LTButtonLoadingPosition) {
  LTButtonLoadingPosition["LEADING"] = "leading";
  LTButtonLoadingPosition["TRAILING"] = "trailing";
})(LTButtonLoadingPosition || (LTButtonLoadingPosition = {}));
var LTButton = function LTButton(_a) {
  var _b, _c, _d, _e;
  var _f = _a.ariaLabel,
    ariaLabel = _f === void 0 ? undefined : _f,
    _g = _a.block,
    block = _g === void 0 ? false : _g,
    _h = _a.children,
    children = _h === void 0 ? null : _h,
    _j = _a.className,
    className = _j === void 0 ? "" : _j,
    _k = _a.disabled,
    disabled = _k === void 0 ? false : _k,
    _l = _a.forwardRef,
    forwardRef = _l === void 0 ? null : _l,
    _m = _a.id,
    id = _m === void 0 ? "" : _m,
    _o = _a.label,
    label = _o === void 0 ? "" : _o,
    _p = _a.loaderPosition,
    loaderPosition = _p === void 0 ? LTButton.LoadingPosition.LEADING : _p,
    _q = _a.loading,
    loading = _q === void 0 ? false : _q,
    _r = _a.leadingIcon,
    leadingIcon = _r === void 0 ? null : _r,
    _s = _a.onClick,
    onClick = _s === void 0 ? function () {} : _s,
    _t = _a.size,
    size = _t === void 0 ? LTButton.Size.SMALL : _t,
    _u = _a.style,
    style = _u === void 0 ? {} : _u,
    _v = _a.trailingIcon,
    trailingIcon = _v === void 0 ? null : _v,
    _w = _a.type,
    type = _w === void 0 ? LTButton.Type.DEFAULT : _w,
    _x = _a.variant,
    variant = _x === void 0 ? LTButton.Variant.OUTLINE : _x,
    btnType = _a.btnType,
    rest = __rest(_a, ["ariaLabel", "block", "children", "className", "disabled", "forwardRef", "id", "label", "loaderPosition", "loading", "leadingIcon", "onClick", "size", "style", "trailingIcon", "type", "variant", "btnType"]);
  var colorScheme = useTheme().colorScheme;
  var DisabledBgColor = (_b = {}, _b[LTButton.Variant.PRIMARY] = "#95d8a6", _b);
  var DisabledBorderColorStyle = (_c = {}, _c[LTButton.Variant.DEFAULT] = "#818b981a", _c[LTButton.Variant.DANGER] = "#818b981a", _c[LTButton.Variant.PRIMARY] = "#95d8a6", _c);
  var DisabledTextColorStyle = (_d = {}, _d[LTButton.Variant.DEFAULT] = "#818b98", _d[LTButton.Variant.PRIMARY] = "#ffffffcc", _d[LTButton.Variant.DANGER] = "#d1242f80", _d[LTButton.Variant.INVISIBLE] = "#818b98", _d);
  var TextColorStyle = (_e = {}, _e[LTButton.Variant.DEFAULT] = "#25292e", _e[LTButton.Variant.INVISIBLE] = "#25292e", _e);
  var ButtonStyle = __assign({
    span: {
      display: "flex",
      alignItems: "center"
    },
    "svg:last-child": {
      marginLeft: "4px"
    },
    "svg:first-child": {
      marginRight: "4px"
    }
  }, colorScheme !== ColorSchemes.DARK && {
    color: TextColorStyle[variant] || "",
    "&:disabled": {
      borderColor: DisabledBorderColorStyle[variant] || "",
      backgroundColor: DisabledBgColor[variant] || "#eff2f5",
      color: DisabledTextColorStyle[variant] || ""
    }
  });
  var FloatingButtonStyle = {
    alignItems: "center",
    borderRadius: "26px",
    bottom: "24px",
    boxShadow: "0px 48px 96px -24px rgba(66, 74, 83, 0.08), 0px 24px 48px -12px rgba(66, 74, 83, 0.08), 0px 4px 32px -4px rgba(66, 74, 83, 0.08), 0px 8px 16px -4px rgba(66, 74, 83, 0.08);",
    display: "flex",
    gap: "4px",
    position: "absolute",
    right: "24px",
    width: "max-content"
  };
  var handleClick = function handleClick(event) {
    if (!loading) {
      onClick(event);
    }
  };
  return /*#__PURE__*/React.createElement(Button, __assign({
    "aria-label": ariaLabel,
    "aria-disabled": disabled,
    block: block,
    className: className,
    disabled: disabled,
    id: id,
    loading: loading
  }, btnType && {
    type: btnType
  }, {
    onClick: handleClick,
    ref: forwardRef,
    sx: __assign(__assign(__assign({}, ButtonStyle), style), type === LTButton.Type.DEFAULT ? {} : FloatingButtonStyle),
    size: size,
    variant: variant
  }, rest), !!loading && loaderPosition === LTButton.LoadingPosition.LEADING && /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Spinner, {
    sx: {
      marginRight: themeGet("space.2")
    },
    size: "small"
  })), !!leadingIcon && /*#__PURE__*/React.createElement("span", {
    style: {
      marginRight: "4px"
    }
  }, leadingIcon), label ? label : children, !!trailingIcon && trailingIcon, !!loading && loaderPosition === LTButton.LoadingPosition.TRAILING && /*#__PURE__*/React.createElement(Spinner, {
    size: "small"
  }));
};
// Attach enums to the component for external use
LTButton.Size = LTButtonSize;
LTButton.Variant = LTButtonVariant;
LTButton.Type = LTButtonType;
LTButton.LoadingPosition = LTButtonLoadingPosition;

export { LTButton as default };
